/**
 * iter228 · OrderBookGrid
 *
 * Excel-style operational grid: rows = one SKU line per Sales Order or
 * Buffer plan. Every row exposes Red/Green/Blue quantities computed on
 * the backend (services/production_grid.py) from the same factory_plans
 * + dispatches records the Rolling / Kanban views use.
 *
 * Features (per user spec):
 *   • Sticky header + pinnable columns
 *   • User-configurable column visibility, order, width (per-user saved)
 *   • Filters: needs-production / ready / dispatched / at-risk
 *   • Search
 *   • Expandable row detail with the underlying plans + dispatches
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Search, Pin, PinOff, Eye, EyeOff, RotateCw,
  Filter as FilterIcon, Save, ArrowUp, ArrowDown, AlertTriangle,
} from 'lucide-react';
import { fmtQty } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Column definitions ─────────────────────────────────────────────
const ALL_COLUMNS = [
  { k: 'customer_name',       l: 'Customer',       w: 160, pin: true,  align: 'left'   },
  { k: 'product_name',        l: 'Product',        w: 200, pin: true,  align: 'left'   },
  { k: 'product_code',        l: 'Product Code',   w: 100, align: 'left',   default: false },
  { k: 'pack_size',           l: 'Pack Size',      w: 80,  align: 'left',   default: false },
  { k: 'sales_order_number',  l: 'SO Number',      w: 110, align: 'left'   },
  { k: 'sales_order_date',    l: 'SO Date',        w: 90,  align: 'left'   },
  { k: 'invoice_number',      l: 'Invoice',        w: 100, align: 'left',   default: false },
  { k: 'ordered_qty',         l: 'Ordered',        w: 85,  align: 'right'  },
  { k: 'left_to_make',        l: 'Left to Make',   w: 100, align: 'right', color: 'red'    },
  { k: 'available_made',      l: 'Available/Made', w: 100, align: 'right', color: 'green'  },
  { k: 'dispatched',          l: 'Dispatched',     w: 90,  align: 'right', color: 'blue'   },
  { k: 'produced_total',      l: 'Total Produced', w: 100, align: 'right', default: false },
  { k: 'planned_qty',         l: 'Planned Qty',    w: 90,  align: 'right', default: false },
  { k: 'planned_batches',     l: 'Batches',        w: 65,  align: 'right', default: false },
  { k: 'required_date',       l: 'Required Date',  w: 105, align: 'left'   },
  { k: 'row_status',          l: 'Status',         w: 120, align: 'left'   },
];

const DEFAULT_PREFS = {
  visible_columns: ALL_COLUMNS.filter(c => c.default !== false).map(c => c.k),
  column_order: ALL_COLUMNS.map(c => c.k),
  pinned_columns: ['customer_name', 'product_name'],
  quick_filter: 'all', // all | needs | ready | dispatched | at_risk
};

const STATUS_LABEL = {
  needs_production: { l: 'Needs Production', klass: 'bg-red-500/15 text-red-300' },
  partial_production: { l: 'Partial Production', klass: 'bg-orange-500/15 text-orange-300' },
  ready: { l: 'Ready', klass: 'bg-emerald-500/15 text-emerald-300' },
  ready_dispatch: { l: 'Ready for Dispatch', klass: 'bg-cyan-500/15 text-cyan-300' },
  dispatched: { l: 'Dispatched', klass: 'bg-blue-500/15 text-blue-300' },
  closed: { l: 'Closed', klass: 'bg-zinc-700/40 text-zinc-400' },
};

const cellTone = (col, val) => {
  if (!col.color) return '';
  if (col.color === 'red')   return val > 0.001 ? 'text-red-300 bg-red-500/5' : 'text-zinc-600';
  if (col.color === 'green') return val > 0.001 ? 'text-emerald-300 bg-emerald-500/5' : 'text-zinc-600';
  if (col.color === 'blue')  return val > 0.001 ? 'text-blue-300 bg-blue-500/5' : 'text-zinc-600';
  return '';
};

export default function OrderBookGrid() {
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});   // { row_id: true }
  const [sort, setSort] = useState({ k: null, dir: 'asc' });
  const [showColMgr, setShowColMgr] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/factory-plans/grid/order-book`, {
        params: { q: q || undefined },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load grid');
    } finally { setLoading(false); }
  }, [q]);

  // Load user preferences
  useEffect(() => {
    axios.get(`${API}/factory-plans/grid/prefs`, { params: { view: 'order_book' }, withCredentials: true })
      .then(r => {
        if (r.data?.prefs && Object.keys(r.data.prefs).length) {
          setPrefs({ ...DEFAULT_PREFS, ...r.data.prefs });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const savePrefs = async () => {
    setPrefsSaving(true);
    try {
      await axios.post(`${API}/factory-plans/grid/prefs`,
        { view: 'order_book', prefs }, { withCredentials: true });
      toast.success('View saved for you');
    } catch (e) { toast.error('Failed to save preferences'); }
    finally { setPrefsSaving(false); }
  };
  const resetPrefs = () => {
    setPrefs(DEFAULT_PREFS);
    toast.info('Reset to default view');
  };

  // Apply filters + sort
  const filteredRows = useMemo(() => {
    let rows = [...(data.rows || [])];
    if (prefs.quick_filter === 'needs')      rows = rows.filter(r => r.left_to_make > 0.001);
    if (prefs.quick_filter === 'ready')      rows = rows.filter(r => r.available_made > 0.001 && r.left_to_make <= 0.001);
    if (prefs.quick_filter === 'dispatched') rows = rows.filter(r => r.dispatched > 0.001);
    if (prefs.quick_filter === 'at_risk')    rows = rows.filter(r => r.at_risk);
    if (sort.k) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const va = a[sort.k], vb = b[sort.k];
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    return rows;
  }, [data, prefs.quick_filter, sort]);

  const visibleCols = useMemo(() => {
    const chosen = prefs.column_order
      .map(k => ALL_COLUMNS.find(c => c.k === k))
      .filter(Boolean)
      .filter(c => prefs.visible_columns.includes(c.k));
    // Safety fallback — never render an empty header (e.g. legacy prefs)
    if (chosen.length === 0) {
      return ALL_COLUMNS.filter(c => c.default !== false);
    }
    return chosen;
  }, [prefs.column_order, prefs.visible_columns]);

  const pinnedCols = useMemo(() => (
    visibleCols.filter(c => prefs.pinned_columns.includes(c.k))
  ), [visibleCols, prefs.pinned_columns]);
  const unpinnedCols = useMemo(() => (
    visibleCols.filter(c => !prefs.pinned_columns.includes(c.k))
  ), [visibleCols, prefs.pinned_columns]);

  const cellVal = (row, col) => {
    const v = row[col.k];
    if (col.k === 'ordered_qty' || col.k === 'left_to_make' || col.k === 'available_made' ||
        col.k === 'dispatched' || col.k === 'produced_total' || col.k === 'planned_qty') {
      return fmtQty(v, row.unit);
    }
    if (col.k === 'row_status') {
      const s = STATUS_LABEL[v] || { l: v, klass: 'bg-zinc-700/40 text-zinc-400' };
      return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.klass}`}>{s.l}</span>;
    }
    if (col.k === 'required_date' && row.at_risk) {
      return <span className="text-red-400 font-semibold flex items-center gap-0.5"><AlertTriangle size={10} /> {v || '—'}</span>;
    }
    return v || '—';
  };

  const toggleColumn = (k) => setPrefs(p => ({
    ...p,
    visible_columns: p.visible_columns.includes(k)
      ? p.visible_columns.filter(x => x !== k)
      : [...p.visible_columns, k],
  }));
  const togglePin = (k) => setPrefs(p => ({
    ...p,
    pinned_columns: p.pinned_columns.includes(k)
      ? p.pinned_columns.filter(x => x !== k)
      : [...p.pinned_columns, k],
  }));

  const bumpSort = (k) => setSort(s => {
    if (s.k !== k) return { k, dir: 'asc' };
    if (s.dir === 'asc') return { k, dir: 'desc' };
    return { k: null, dir: 'asc' };
  });

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-2 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-1 min-w-[220px] bg-zinc-800 rounded-lg px-2.5 py-1.5">
          <Search size={12} className="text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search customer / product / SO number…" data-testid="fp-grid-search"
            className="w-full bg-transparent text-xs text-zinc-100 focus:outline-none" />
        </div>

        <QuickFilter active={prefs.quick_filter === 'all'} onClick={() => setPrefs(p => ({ ...p, quick_filter: 'all' }))} label="All" testid="fp-grid-filter-all" />
        <QuickFilter active={prefs.quick_filter === 'needs'} onClick={() => setPrefs(p => ({ ...p, quick_filter: 'needs' }))} label="🔴 Needs Production" testid="fp-grid-filter-needs" />
        <QuickFilter active={prefs.quick_filter === 'ready'} onClick={() => setPrefs(p => ({ ...p, quick_filter: 'ready' }))} label="🟢 Ready" testid="fp-grid-filter-ready" />
        <QuickFilter active={prefs.quick_filter === 'dispatched'} onClick={() => setPrefs(p => ({ ...p, quick_filter: 'dispatched' }))} label="🔵 Dispatched" testid="fp-grid-filter-dispatched" />
        <QuickFilter active={prefs.quick_filter === 'at_risk'} onClick={() => setPrefs(p => ({ ...p, quick_filter: 'at_risk' }))} label="⚠ At Risk" testid="fp-grid-filter-at-risk" />

        <button onClick={() => setShowColMgr(v => !v)} data-testid="fp-grid-cols-btn"
          className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs px-2.5 py-1.5 rounded-lg border border-zinc-700">
          <FilterIcon size={11} /> Columns
        </button>
        <button onClick={savePrefs} disabled={prefsSaving} data-testid="fp-grid-save-view-btn"
          className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black text-xs font-semibold px-2.5 py-1.5 rounded-lg">
          <Save size={11} /> {prefsSaving ? 'Saving…' : 'Save My View'}
        </button>
        <button onClick={resetPrefs} className="text-xs text-zinc-500 hover:text-zinc-300 px-2 py-1.5">Reset</button>
        <button onClick={fetchData} title="Refresh" className="text-zinc-500 hover:text-zinc-200 p-1.5">
          <RotateCw size={13} />
        </button>
      </div>

      {/* Column manager panel */}
      {showColMgr && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
          <p className="text-[10px] uppercase text-zinc-400 tracking-wider mb-2">Choose columns to show / pin</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {ALL_COLUMNS.map(c => {
              const visible = prefs.visible_columns.includes(c.k);
              const pinned = prefs.pinned_columns.includes(c.k);
              return (
                <div key={c.k} className="flex items-center gap-1 bg-zinc-800/50 rounded-lg px-2 py-1">
                  <button onClick={() => toggleColumn(c.k)} className={`text-zinc-400 hover:text-zinc-100 ${visible ? 'text-emerald-400' : ''}`}
                    title={visible ? 'Hide column' : 'Show column'}>
                    {visible ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                  <button onClick={() => togglePin(c.k)} className={`text-zinc-400 hover:text-zinc-100 ${pinned ? 'text-amber-400' : ''}`}
                    title={pinned ? 'Unpin column' : 'Pin column'}>
                    {pinned ? <Pin size={12} /> : <PinOff size={12} />}
                  </button>
                  <span className="text-xs text-zinc-200 truncate">{c.l}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary strip */}
      {data.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Kpi l="Rows"           v={data.summary.rows}                       tone="text-zinc-100" />
          <Kpi l="Left to Make"   v={fmtQty(data.summary.total_left_to_make)}   tone="text-red-400" />
          <Kpi l="Available/Made" v={fmtQty(data.summary.total_available_made)} tone="text-emerald-400" />
          <Kpi l="Dispatched"     v={fmtQty(data.summary.total_dispatched)}     tone="text-blue-400" />
          <Kpi l="At Risk"        v={data.summary.at_risk}                      tone={data.summary.at_risk > 0 ? 'text-yellow-400' : 'text-zinc-400'} />
        </div>
      )}

      {/* Grid */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
          <table className="text-xs" data-testid="fp-order-book-table">
            <thead className="sticky top-0 z-10 bg-zinc-900">
              <tr>
                <th className="px-1 py-2 w-6 border-r border-zinc-800"></th>
                {[...pinnedCols, ...unpinnedCols].map((c, idx) => (
                  <th key={c.k}
                    onClick={() => bumpSort(c.k)}
                    className={`px-2 py-2 text-[10px] uppercase font-semibold text-zinc-400 tracking-wider whitespace-nowrap cursor-pointer border-b border-zinc-700 select-none ${
                      c.align === 'right' ? 'text-right' : 'text-left'
                    } ${prefs.pinned_columns.includes(c.k) ? 'sticky bg-zinc-900 z-20' : ''}`}
                    style={{ minWidth: c.w, left: prefs.pinned_columns.includes(c.k) ? `${idx * 0 + [...pinnedCols].slice(0, [...pinnedCols].findIndex(p => p.k === c.k)).reduce((s, p) => s + p.w, 24)}px` : undefined }}>
                    <span className="inline-flex items-center gap-1">
                      {c.l}
                      {sort.k === c.k && (sort.dir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={visibleCols.length + 1} className="text-center py-8 text-zinc-500">Loading…</td></tr>}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={visibleCols.length + 1} className="text-center py-10 text-zinc-600">No rows match filters.</td></tr>
              )}
              {!loading && filteredRows.map(row => {
                const isOpen = expanded[row.row_id];
                return (
                  <React.Fragment key={row.row_id}>
                    <tr data-testid={`fp-grid-row-${row.row_id}`}
                      className={`border-b border-zinc-800/60 hover:bg-zinc-800/30 ${row.at_risk ? 'bg-red-500/[0.03]' : ''}`}>
                      <td className="px-1 py-1.5 border-r border-zinc-800/60">
                        <button onClick={() => setExpanded(x => ({ ...x, [row.row_id]: !x[row.row_id] }))}
                          className="text-zinc-500 hover:text-zinc-100">
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </button>
                      </td>
                      {[...pinnedCols, ...unpinnedCols].map((c) => {
                        const pinned = prefs.pinned_columns.includes(c.k);
                        const val = row[c.k];
                        return (
                          <td key={c.k}
                            className={`px-2 py-1.5 text-xs whitespace-nowrap ${
                              c.align === 'right' ? 'text-right font-mono' : 'text-zinc-200'
                            } ${cellTone(c, val)} ${pinned ? 'sticky bg-zinc-900 z-[1]' : ''}`}
                            style={{ minWidth: c.w }}>
                            {cellVal(row, c)}
                          </td>
                        );
                      })}
                    </tr>
                    {isOpen && <ExpandedRow row={row} colspan={visibleCols.length + 1} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExpandedRow({ row, colspan }) {
  return (
    <tr className="bg-zinc-900/60">
      <td colSpan={colspan} className="px-6 py-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Red: Production Pending */}
          <div>
            <p className="text-[10px] uppercase text-red-400 tracking-wider mb-1.5">🔴 Production Pending · {fmtQty(row.left_to_make, row.unit)}</p>
            {row.left_to_make <= 0.001 ? (
              <p className="text-[11px] text-zinc-600">Nothing pending.</p>
            ) : (
              <div className="space-y-1">
                {(row.plans || []).filter(p => (p.completed_quantity || 0) < (p.quantity || 0)).map(p => (
                  <div key={p.id} className="text-[11px] text-zinc-300">
                    <span className="text-red-400 font-mono">{fmtQty((p.quantity || 0) - (p.completed_quantity || 0))}</span> · {p.planned_date} · {p.status}
                  </div>
                ))}
                {row.left_to_make > 0.001 && (row.plans || []).filter(p => (p.completed_quantity || 0) < (p.quantity || 0)).length === 0 && (
                  <p className="text-[11px] text-yellow-400">⚠ Not yet planned</p>
                )}
              </div>
            )}
          </div>
          {/* Green: Produced / Available */}
          <div>
            <p className="text-[10px] uppercase text-emerald-400 tracking-wider mb-1.5">🟢 Available / Produced · {fmtQty(row.available_made, row.unit)}</p>
            {(row.plans || []).filter(p => (p.completed_quantity || 0) > 0).map(p => (
              <div key={p.id} className="text-[11px] text-zinc-300">
                <span className="text-emerald-400 font-mono">{fmtQty(p.completed_quantity)}</span> · {p.id} · {p.planned_date}
              </div>
            ))}
            {(row.plans || []).filter(p => (p.completed_quantity || 0) > 0).length === 0 && (
              <p className="text-[11px] text-zinc-600">Nothing produced yet.</p>
            )}
          </div>
          {/* Blue: Dispatched */}
          <div>
            <p className="text-[10px] uppercase text-blue-400 tracking-wider mb-1.5">🔵 Dispatched · {fmtQty(row.dispatched, row.unit)}</p>
            {(row.dispatches || []).length === 0 ? (
              <p className="text-[11px] text-zinc-600">No dispatches yet.</p>
            ) : (
              (row.dispatches || []).map((d, i) => (
                <div key={i} className="text-[11px] text-zinc-300">
                  <span className="text-blue-400 font-mono">{fmtQty(d.qty)}</span> · {d.id} · {d.date} · {d.status}
                </div>
              ))
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function QuickFilter({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
        active
          ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
          : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-zinc-200'
      }`}>
      {label}
    </button>
  );
}

function Kpi({ l, v, tone }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-2.5">
      <p className="text-[10px] uppercase text-zinc-500 tracking-wider">{l}</p>
      <p className={`text-lg font-heading font-bold ${tone}`}>{v}</p>
    </div>
  );
}
